import React, { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { Skeleton, SparkIcon, TooltipTrigger } from '@spark';
import MappedValueHighlight from '@/shared/components/MappedValueHighlight';
import TemplateEditSurface from '@/shared/components/TemplateEditSurface';
import {
    alignTemplateToText,
    buildMappedTemplate,
    fieldsInTemplate,
    parseTemplateSegments,
} from './promptMappingUtils';

const FieldPill = ({ name, kind, isPrimary, onRemove }) => (
    <span
        className={[
            'cw-pm-pill',
            kind === 'custom' ? 'cw-pm-pill--custom' : 'cw-pm-pill--column',
            isPrimary ? 'cw-pm-pill--primary' : '',
        ]
            .filter(Boolean)
            .join(' ')}
        data-field={name}
        contentEditable={false}
    >
        {isPrimary && <SparkIcon slug="pin-filled" size="small" className="cw-pm-pill__pin" />}
        <span className="cw-pm-pill__text">{`{{${name}}}`}</span>
        {onRemove ? (
            // Hover-revealed X (custom pills only): unmaps the field, putting
            // its original text back. Serialization ignores pill innards (it
            // reads data-field), so the button never leaks into the template.
            <TooltipTrigger text="Remove from template" className="cw-pm-pill__remove">
                <span
                    role="button"
                    tabIndex={0}
                    aria-label={`Remove ${name} from template`}
                    className="cw-pm-pill__remove-btn"
                    onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        onRemove();
                    }}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            event.stopPropagation();
                            onRemove();
                        }
                    }}
                >
                    <SparkIcon slug="close" size="small" />
                </span>
            </TooltipTrigger>
        ) : null}
    </span>
);

/**
 * The Map-prompt-template text surface.
 *
 * Edit mode delegates to the shared TemplateEditSurface (flat contentEditable
 * with non-editable pill spans, template serialization, Enter/paste splices,
 * undo/redo); external mutations (panel insert, unmap, shuffle, primary
 * change) bump `renderVersion`, which remounts the surface from the new
 * template.
 *
 * "Show original text" mode renders the sampled record's raw example text
 * with the currently-mapped values underlined.
 */
const PromptTemplateEditor = ({
    mode,
    template,
    originalText,
    recordValues,
    customFields,
    primaryField,
    renderVersion,
    /** Shuffle in flight: static text stays, field values become skeletons. */
    shuffling = false,
    onTemplateChange,
    /** Fired with the caret's template-string offset on every caret move. */
    onCaretChange,
    /** Fired with `{ text, start, end, rect }` on a text selection, or null. */
    onSelectText,
    /** Fired with `{ triggerLength, offset, rect }` when `{{` or `/` is typed, or null. */
    onInsertTrigger,
    /** Fired with the field name when a custom pill's X is clicked. */
    onRemoveField,
}) => {
    const fieldKind = useCallback(
        (name) =>
            Object.prototype.hasOwnProperty.call(customFields || {}, name) ? 'custom' : 'column',
        [customFields]
    );

    // Same ref pattern as the surface's templateRef: the memoized pills must
    // always call the latest handler without recomputing when its identity
    // changes.
    const onRemoveFieldRef = useRef(onRemoveField);
    onRemoveFieldRef.current = onRemoveField;

    const renderEditChildren = useCallback(
        (currentTemplate) =>
            parseTemplateSegments(currentTemplate).map((segment, index) =>
                segment.type === 'field' ? (
                    <FieldPill
                        key={index}
                        name={segment.name}
                        kind={fieldKind(segment.name)}
                        isPrimary={segment.name === primaryField}
                        onRemove={
                            fieldKind(segment.name) === 'custom'
                                ? () => onRemoveFieldRef.current?.(segment.name)
                                : undefined
                        }
                    />
                ) : (
                    <React.Fragment key={index}>{segment.text}</React.Fragment>
                )
            ),
        [fieldKind, primaryField]
    );

    const originalChildren = useMemo(() => {
        if (mode !== 'original') return null;

        // Preferred: align the template's literal anchors against the
        // original text so each {{field}} marks exactly the span it captures
        // — this covers custom fields whose stored value is empty (created
        // from the insert menu) or comes from another record.
        let segments = alignTemplateToText(template, originalText);

        if (!segments) {
            // Fallback when the template's literals no longer fit this text
            // (e.g. the user typed new prose): mark every occurrence of each
            // mapped field's known value instead.
            const valueByField = {};
            fieldsInTemplate(template).forEach((name) => {
                const value = recordValues?.[name] ?? customFields?.[name];
                if (typeof value === 'string' && value.trim()) valueByField[name] = value;
            });
            const { template: marked } = buildMappedTemplate(originalText, valueByField);
            segments = parseTemplateSegments(marked).map((segment) =>
                segment.type === 'field'
                    ? { ...segment, text: valueByField[segment.name] }
                    : segment
            );
        }

        return segments.map((segment, index) =>
            segment.type === 'field' && segment.text ? (
                shuffling ? (
                    // Shuffle: only the mapped spans turn into bars (sized to
                    // the outgoing value) — the static text keeps its place.
                    <Skeleton
                        // eslint-disable-next-line react/no-array-index-key
                        key={index}
                        shapeType="rectangle"
                        width={`${Math.min(Math.max(segment.text.length, 4), 48)}ch`}
                        height={15}
                        className="cw-pm-skeleton cw-pm-original-value-skeleton"
                    />
                ) : (
                    // Hovering a mapped value reveals which field it maps to.
                    <MappedValueHighlight
                        // eslint-disable-next-line react/no-array-index-key
                        key={index}
                        name={segment.name}
                        value={segment.text}
                        kind={fieldKind(segment.name)}
                    />
                )
            ) : (
                // eslint-disable-next-line react/no-array-index-key
                <React.Fragment key={index}>{segment.text}</React.Fragment>
            )
        );
    }, [mode, template, originalText, recordValues, customFields, fieldKind, shuffling]);

    // Shuffle re-renders the original surface in place (skeleton bars in, the
    // new record's values out) without remounting it; re-apply the last scroll
    // position so the reading point survives the content-height changes.
    const originalRef = useRef(null);
    const originalScrollTopRef = useRef(0);
    const handleOriginalScroll = useCallback(() => {
        originalScrollTopRef.current = originalRef.current?.scrollTop ?? 0;
    }, []);
    useLayoutEffect(() => {
        if (mode !== 'original') return;
        const root = originalRef.current;
        if (root) root.scrollTop = originalScrollTopRef.current;
        // `shuffling` / `originalText` mark the shuffle transitions that
        // reflow the content — they only trigger the restore.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, shuffling, originalText]);

    if (mode === 'original') {
        return (
            <div
                ref={originalRef}
                className="cw-pm-editor cw-pm-editor--original"
                onScroll={handleOriginalScroll}
            >
                {originalChildren}
            </div>
        );
    }

    return (
        <TemplateEditSurface
            template={template}
            renderVersion={renderVersion}
            editable={!shuffling}
            className={`cw-pm-editor cw-pm-editor--edit${shuffling ? ' cw-pm-editor--shuffling' : ''}`}
            ariaLabel="Prompt template"
            autoFocus
            renderChildren={renderEditChildren}
            childrenDeps={[primaryField, fieldKind]}
            onTemplateChange={onTemplateChange}
            onCaretChange={onCaretChange}
            onSelectText={onSelectText}
            onInsertTrigger={onInsertTrigger}
        />
    );
};

export default PromptTemplateEditor;
